import {
  FunctionComponent,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  Dispatch,
  useEffect,
  ReactNode,
  SetStateAction,
} from 'react'
import type {
  OnlineOperationalMenuItem,
  OnlineOperationalMenuRefItem,
  OperationalMenu,
} from '@/libs/helpers/adapters'
import type { CartChildItem, CartItem } from '@/libs/helpers/utils'

interface ModifierValidationErrors {
  [name: string]: boolean
}

export interface ContextType {
  setModifiers: Dispatch<SetStateAction<CartItem['childItems']>>
  setErrors: Dispatch<SetStateAction<ModifierValidationErrors>>
  modifiers: CartItem['childItems']
  setMenuItem: Dispatch<SetStateAction<OnlineOperationalMenuItemCtxPayload | null>>
  setItemIndex: Dispatch<SetStateAction<number>>
  errors: ModifierValidationErrors
  menuItem: OnlineOperationalMenuItemCtxPayload | null
  itemIndex: number
  shouldDisplayErrors: boolean
  setShouldDisplayErrors: Dispatch<SetStateAction<boolean>>
  nextRequiredModifierGroupID?: string | null
  validateModifiers: () => void
  openNestedModifier: CartChildItem | null
  setOpenNestedModifier: Dispatch<SetStateAction<CartChildItem | null>>
}

export interface OnlineOperationalMenuItemCtxPayload extends OnlineOperationalMenuItem {
  menuGroupXRefID: string
}

type UseHookType = () => ContextType

const MenuItemContext = createContext<ContextType | null>(null)

const Provider: FunctionComponent<{
  operationalMenu: OperationalMenu
  children?: ReactNode
}> = ({ operationalMenu, children }) => {
  const modifierGroups = useMemo(
    () => operationalMenu?.modifierGroups ?? {},
    [operationalMenu?.modifierGroups]
  )
  const [shouldDisplayErrors, setShouldDisplayErrors] = useState<boolean>(false)
  const [openNestedModifier, setOpenNestedModifier] = useState<CartChildItem | null>(null)
  const [modifiers, setModifiers] = useState<CartItem['childItems']>([])
  const [errors, setErrors] = useState<ModifierValidationErrors>({})
  const [menuItem, setMenuItem] = useState<OnlineOperationalMenuItemCtxPayload | null>(null)
  const [itemIndex, setItemIndex] = useState<number>(-1)
  const [nextRequiredModifierGroupID, setNextRequiredModifierGroupID] = useState<string | null>(
    null
  )

  const getMatchedModifiers = useCallback(
    (modifiers: CartChildItem[], targetModifier: string): CartChildItem[] => {
      let matchedModifiers: CartChildItem[] = []
      for (const modifier of modifiers) {
        if (modifier.itemID === targetModifier) {
          matchedModifiers = modifier.childItems ?? []
          break
        }
        if (modifier.childItems) {
          matchedModifiers = getMatchedModifiers(modifier.childItems, targetModifier)
        }
      }

      return matchedModifiers
    },
    []
  )

  const sumQuantity = useCallback(
    (
      currentOptions: OnlineOperationalMenuRefItem[],
      currentModifierGroupXRefID: string
    ): number => {
      if (!modifiers) {
        return 0
      }
      let modifiersInCart = modifiers

      if (openNestedModifier != null) {
        modifiersInCart = getMatchedModifiers(modifiers ?? [], openNestedModifier.itemID)
      }
      let sum = 0
      modifiersInCart
        .filter((modifier) =>
          currentOptions.some(
            (modifierOption) =>
              modifier.itemID === modifierOption.id &&
              modifier.modifierGroupXRefID === currentModifierGroupXRefID
          )
        )
        .map((option) => Number(option.quantity))
        .forEach((val) => (sum += val))
      return sum
    },
    [getMatchedModifiers, modifiers, openNestedModifier]
  )

  const validateModifiers = useCallback((): void => {
    if (menuItem == null) {
      return
    }
    let availableModifierGroups = menuItem.modifierGroupRefs

    if (openNestedModifier != null) {
      const menuItemXRefID =
        operationalMenu.modifierOptions[openNestedModifier.itemID].menuItemXRefID

      if (menuItemXRefID) {
        availableModifierGroups = operationalMenu.menuItems[menuItemXRefID].modifierGroupRefs
      }
    }

    // Build the validation errors object without using Array.reduce for clarity
    const newErrors: ModifierValidationErrors = {}
    setNextRequiredModifierGroupID(null)
    for (const currentModifierGroupRef of availableModifierGroups) {
      const modifierGroup = modifierGroups[currentModifierGroupRef.id]
      if (modifierGroup == null) {
        continue
      }
      const { XRefID, modifierOptionRefs, selectionMin, selectionMax } = modifierGroup

      const quantitySum = sumQuantity(modifierOptionRefs, XRefID)

      // Validates if the number of modifiers fits the max and min allowed
      const isLessThanMinimum = selectionMin > 0 && quantitySum < selectionMin
      const isMoreThanMaximum = selectionMax > 0 && quantitySum > selectionMax

      // Populate the errors object if invalid
      if (isLessThanMinimum || isMoreThanMaximum) {
        newErrors[XRefID] = true
      }
    }
    setErrors(newErrors)
    availableModifierGroups.every((modifierGroupRef: OnlineOperationalMenuRefItem) => {
      const modifierGroup = modifierGroups[modifierGroupRef.id]
      if (modifierGroup == null || modifierGroup.selectionMin === 0) {
        return true
      }

      const selectedOptions = sumQuantity(modifierGroup.modifierOptionRefs, modifierGroup.XRefID)
      if (selectedOptions < modifierGroup.selectionMin) {
        setNextRequiredModifierGroupID(modifierGroup.XRefID)
      }

      return selectedOptions >= modifierGroup.selectionMin
    })
  }, [
    menuItem,
    modifierGroups,
    openNestedModifier,
    operationalMenu.menuItems,
    operationalMenu.modifierOptions,
    sumQuantity,
  ])

  const ctxValue = useMemo(
    () => ({
      setShouldDisplayErrors,
      shouldDisplayErrors,
      setModifiers,
      setItemIndex,
      setMenuItem,
      setErrors,
      setOpenNestedModifier,
      openNestedModifier,
      modifiers,
      itemIndex,
      menuItem,
      errors,
      nextRequiredModifierGroupID,
      validateModifiers,
    }),
    [
      setShouldDisplayErrors,
      shouldDisplayErrors,
      setModifiers,
      setItemIndex,
      setMenuItem,
      setErrors,
      setOpenNestedModifier,
      openNestedModifier,
      modifiers,
      itemIndex,
      menuItem,
      errors,
      nextRequiredModifierGroupID,
      validateModifiers,
    ]
  )

  useEffect(() => {
    // All the modifier quantity validation magic happens here!!!!!
    validateModifiers()
  }, [validateModifiers])

  return <MenuItemContext.Provider value={ctxValue}>{children}</MenuItemContext.Provider>
}

export default Provider
export const useMenuItem: UseHookType = () => {
  const ctx = useContext(MenuItemContext)

  if (!ctx) {
    throw new Error('useMenuItem must be used within Provider')
  }

  return ctx
}
